@switch (status()) {
  @case ('guest') {
    <div class="community-finder__gate">
      @if (sessionExpired()) {
        <p class="community-finder__gate-alert" role="status">{{ copy.sessionExpired }}</p>
      }
      <h3 class="community-finder__gate-title">{{ copy.guestTitle }}</h3>
      <p class="community-finder__gate-text">{{ copy.guestBody }}</p>
      <div class="community-finder__gate-actions">
        <app-button variant="primary" (click)="openLogin()">{{ copy.guestLogin }}</app-button>
        <app-button variant="secondary" (click)="openRegister()">{{ copy.guestRegister }}</app-button>
      </div>
    </div>
  }

  @case ('unverified') {
    <div class="community-finder__gate">
      <p class="community-finder__gate-title">{{ unverifiedText() }}</p>
      <app-players-resend-verification />
    </div>
  }

  @case ('error') {
    <div class="community-finder__empty" role="alert">
      @if (!errorMascotError()) {
        <img
          src="images/logo/charging_error.png"
          [alt]="copy.errorMascotAlt"
          width="640"
          height="626"
          class="community-finder__mascot"
          loading="lazy"
          (error)="onErrorMascotError()"
        />
      }
      <p class="community-finder__empty-text">{{ copy.errorText }}</p>
      <app-button variant="secondary" (click)="retry()">{{ copy.retry }}</app-button>
    </div>
  }

  @case ('ready') {
    @switch (banner()) {
      @case ('no-profile') {
        <aside class="community-finder__banner">
          <div class="community-finder__banner-body">
            <p class="community-finder__banner-title">{{ bannerCopy.noProfileTitle }}</p>
            <p class="community-finder__banner-text">{{ bannerCopy.noProfileBody }}</p>
          </div>
          <a class="community-finder__banner-cta" [routerLink]="profilePath">{{ bannerCopy.noProfileCta }}</a>
        </aside>
      }
      @case ('hidden') {
        <aside class="community-finder__banner">
          <div class="community-finder__banner-body">
            <p class="community-finder__banner-title">{{ bannerCopy.hiddenTitle }}</p>
            <p class="community-finder__banner-text">{{ bannerCopy.hiddenBody }}</p>
          </div>
          <a class="community-finder__banner-cta" [routerLink]="profilePath">{{ bannerCopy.hiddenCta }}</a>
        </aside>
      }
      @case ('suspended') {
        <aside class="community-finder__banner community-finder__banner--muted">
          <p class="community-finder__banner-text">
            {{ bannerCopy.suspendedBefore }}<a [routerLink]="contactPath">{{ bannerCopy.suspendedLink }}</a>{{ bannerCopy.suspendedAfter }}
          </p>
        </aside>
      }
    }

    <div class="community-finder__toolbar">
      <div class="community-finder__pills" role="group" [attr.aria-label]="copy.gameFilterLabel">
        <button
          type="button"
          class="community-finder__pill"
          [class.is-active]="game() === null"
          [attr.aria-pressed]="game() === null"
          (click)="selectGame(null)"
        >
          {{ copy.allGames }}
        </button>
        @for (option of games(); track option.slug) {
          <button
            type="button"
            class="community-finder__pill"
            [class.is-active]="game() === option.slug"
            [attr.aria-pressed]="game() === option.slug"
            [style.--pill-color]="option.accent_color"
            (click)="selectGame(option.slug)"
          >
            {{ option.name }}
          </button>
        }
      </div>
      <div class="community-finder__select-group">
        <label class="community-finder__select-label" for="player-finder-province">
          {{ copy.provinceFilterLabel }}
        </label>
        <div class="community-finder__select-wrap">
          <select
            id="player-finder-province"
            class="community-finder__select-input"
            [value]="province() ?? ''"
            (change)="onProvinceChange($event)"
          >
            <option value="">{{ copy.allProvinces }}</option>
            @for (option of provinces; track option.slug) {
              <option [value]="option.slug">{{ option.name }}</option>
            }
          </select>
          <span class="community-finder__select-chevron" aria-hidden="true">
            <app-icon [icon]="chevronDownIcon" [size]="16" />
          </span>
        </div>
      </div>
    </div>

    @if (loading()) {
      <ng-container [ngTemplateOutlet]="skeletonGrid" />
    } @else if (cards().length > 0) {
      @if (sentNotice(); as notice) {
        <p #sentNotice class="community-finder__sent" role="status" tabindex="-1">{{ notice }}</p>
      }
      <div class="community-finder__grid">
        @for (card of cards(); track card.alias) {
          <app-player-card [card]="card" [inviteEnabled]="true" (invite)="openInvite($event)" />
        }
      </div>
      @if (meta(); as m) {
        @if (m.total_pages > 1) {
          <div class="community-finder__pagination">
            <app-pagination
              [currentPage]="m.page"
              [totalPages]="m.total_pages"
              (pageChange)="goToPage($event)"
            />
          </div>
        }
      }
    } @else {
      <div class="community-finder__empty">
        @if (!emptyMascotError()) {
          <img
            src="images/logo/community_lupa.png"
            [alt]="copy.emptyMascotAlt"
            width="460"
            height="446"
            class="community-finder__mascot"
            loading="lazy"
            (error)="onEmptyMascotError()"
          />
        }
        <p class="community-finder__empty-text">{{ emptyText() }}</p>
        @if (showEmptyCta()) {
          <a class="community-finder__empty-cta" [routerLink]="profilePath">{{ copy.emptyCta }}</a>
        }
      </div>
    }

    <p class="community-finder__report">
      <a [routerLink]="contactPath">{{ copy.report }}</a>
    </p>

    @if (inviteTarget(); as target) {
      <app-play-request-modal
        [card]="target"
        [sender]="sender()"
        (sent)="onInviteSent($event)"
        (notFound)="onInviteNotFound()"
        (closed)="closeInvite()"
      />
    }
  }

  @default {
    <!-- pending: el mismo skeleton en el SSR y en el primer render del cliente (N3). -->
    <div class="community-finder__toolbar" aria-hidden="true">
      <span class="community-finder__skeleton-bar community-finder__skeleton-bar--pills"></span>
      <span class="community-finder__skeleton-bar community-finder__skeleton-bar--select"></span>
    </div>
    <ng-container [ngTemplateOutlet]="skeletonGrid" />
  }
}

<ng-template #skeletonGrid>
  <!-- D28 b: un aria-label en un div sin rol no se anuncia; role=status + texto oculto. -->
  <div class="community-finder__grid" role="status" aria-busy="true">
    <span class="community-finder__sr-only">{{ copy.skeletonLabel }}</span>
    @for (item of skeletonCards; track item) {
      <div class="community-finder__skeleton-card" aria-hidden="true">
        <div class="community-finder__skeleton-head">
          <span class="community-finder__skeleton-avatar"></span>
          <span class="community-finder__skeleton-lines">
            <span class="community-finder__skeleton-line community-finder__skeleton-line--name"></span>
            <span class="community-finder__skeleton-line community-finder__skeleton-line--meta"></span>
          </span>
        </div>
        <span class="community-finder__skeleton-line community-finder__skeleton-line--pills"></span>
        <span class="community-finder__skeleton-line community-finder__skeleton-line--bio"></span>
      </div>
    }
  </div>
</ng-template>
